import { Controller } from "@hotwired/stimulus"

/**
 * Bag Checkout Summary Controller
 * 
 * Manages the display of summary information for completed sections.
 * Handles:
 * - Contact summary (email)
 * - Shipping/Billing summary (addresses)
 * - Summary visibility toggling
 */
export default class extends Controller {
    static targets = [
        "contactSummaryBox",
        "contactEmail",
        "shippingSummaryBox",
        "shippingName",
        "shippingAddress",
        "shippingPhone",
        "billingName",
        "billingAddress",
        "billingPhone",
        "billToShipCheckbox"
    ]

    connect() {
        console.log('Bag checkout summary controller connected');
        
        // Listen for events from state controller
        this.element.addEventListener('bag--checkout-state:showSummary', this.handleShowSummary.bind(this));
        this.element.addEventListener('bag--checkout:sectionEditing', this.handleSectionEditing.bind(this));
    }
    
    disconnect() {
        this.element.removeEventListener('bag--checkout-state:showSummary', this.handleShowSummary.bind(this));
        this.element.removeEventListener('bag--checkout:sectionEditing', this.handleSectionEditing.bind(this));
    }

    /**
     * Handle show summary events
     */
    handleShowSummary(event) {
        const { section, summary } = event.detail;
        
        if (section === 1) {
            this.populateContactSummary(summary);
        } else if (section === 2) {
            this.populateShippingSummary();
        }
    }

    /**
     * Handle section editing events
     */
    handleSectionEditing(event) {
        const { section } = event.detail;
        
        // Hide summaries for sections being edited and all later sections
        if (section <= 1 && this.hasContactSummaryBoxTarget) {
            this.contactSummaryBoxTarget.style.display = 'none';
        }
        if (section <= 2 && this.hasShippingSummaryBoxTarget) {
            this.shippingSummaryBoxTarget.style.display = 'none';
        }
    }

    /**
     * Populate contact summary
     */
    populateContactSummary(email) {
        if (this.hasContactSummaryBoxTarget && this.hasContactEmailTarget) {
            this.contactEmailTarget.textContent = email;
            this.contactSummaryBoxTarget.style.display = 'block';
        }
    }

    /**
     * Populate shipping summary
     */
    populateShippingSummary() {
        const firstName = this.getFieldValue('[name="order[ship_firstname]"]');
        const lastName = this.getFieldValue('[name="order[ship_lastname]"]');
        const address1 = this.getFieldValue('[name="order[ship_address1]"]');
        const address2 = this.getFieldValue('[name="order[ship_address2]"]');
        const city = this.getFieldValue('[name="order[ship_city]"]');
        const state = this.getFieldValue('[name="order[ship_state]"]');
        const zipcode = this.getFieldValue('[name="order[ship_zipcode]"]');
        const country = this.getFieldValue('[name="order[ship_country]"]');
        const phone = this.getFieldValue('[name="order[ship_dayphone]"]');
        
        const billToShip = this.hasBillToShipCheckboxTarget ? this.billToShipCheckboxTarget.checked : false;
        
        if (this.hasShippingSummaryBoxTarget) {
            // Shipping address
            if (this.hasShippingNameTarget) {
                this.shippingNameTarget.textContent = `${firstName} ${lastName}`;
            }
            if (this.hasShippingAddressTarget) {
                const address2Line = address2 ? `${address2}<br>` : '';
                this.shippingAddressTarget.innerHTML = `${address1}<br>${address2Line}${city}, ${state} ${zipcode}<br>${country}`;
            }
            if (this.hasShippingPhoneTarget) {
                this.shippingPhoneTarget.textContent = phone;
            }
            
            // Billing address
            if (billToShip) {
                if (this.hasBillingNameTarget) {
                    this.billingNameTarget.innerHTML = '';
                }
                if (this.hasBillingAddressTarget) {
                    this.billingAddressTarget.innerHTML = '<span class="text-muted fst-italic">Same as delivery address</span>';
                }
                if (this.hasBillingPhoneTarget) {
                    this.billingPhoneTarget.textContent = '';
                }
            } else {
                const billFirstName = this.getFieldValue('[name="order[bill_firstname]"]');
                const billLastName = this.getFieldValue('[name="order[bill_lastname]"]');
                const billAddress1 = this.getFieldValue('[name="order[bill_address1]"]');
                const billAddress2 = this.getFieldValue('[name="order[bill_address2]"]');
                const billCity = this.getFieldValue('[name="order[bill_city]"]');
                const billState = this.getFieldValue('[name="order[bill_state]"]');
                const billZipcode = this.getFieldValue('[name="order[bill_zipcode]"]');
                const billCountry = this.getFieldValue('[name="order[bill_country]"]');
                const billPhone = this.getFieldValue('[name="order[bill_dayphone]"]');
                
                if (billFirstName || billLastName || billAddress1) {
                    if (this.hasBillingNameTarget) {
                        this.billingNameTarget.textContent = `${billFirstName} ${billLastName}`;
                    }
                    if (this.hasBillingAddressTarget) {
                        const billAddress2Line = billAddress2 ? `${billAddress2}<br>` : '';
                        this.billingAddressTarget.innerHTML = `${billAddress1}<br>${billAddress2Line}${billCity}, ${billState} ${billZipcode}<br>${billCountry}`;
                    }
                    if (this.hasBillingPhoneTarget) {
                        this.billingPhoneTarget.textContent = billPhone;
                    }
                } else {
                    if (this.hasBillingNameTarget) {
                        this.billingNameTarget.innerHTML = '';
                    }
                    if (this.hasBillingAddressTarget) {
                        this.billingAddressTarget.innerHTML = '<span class="text-muted fst-italic">Same as delivery address</span>';
                    }
                    if (this.hasBillingPhoneTarget) {
                        this.billingPhoneTarget.textContent = '';
                    }
                }
            }
            
            this.shippingSummaryBoxTarget.style.display = 'block';
        }
    }

    /**
     * Helper to get field value safely
     */
    getFieldValue(selector) {
        return this.element.querySelector(selector)?.value || '';
    }
}
